import { Bell, ChartBar, Envelope, Percent, X as XIcon } from 'phosphor-react';
import React, { useEffect, useMemo, useState } from 'react';
import { DillButton, DillCard, DillInput, DillModal, DillToggle } from '../../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import {
  generateAiNotificationRecommendation,
  updateBuyerEmailPreferences,
} from '../../../../../redux/services/supplierBuyersService';
import {
  AiNotificationMode,
  AiNotificationReminderType,
  AiNotificationSlot,
  Buyer,
} from '@dill/dill-shared';
import { openDillToast } from '../../../../../utils/helpers';
import { COLORS } from '../../../../../utils/colors';
import { APP_ENVIRONMENT } from '../../../../../utils/constants';
import AgenticNotificationPreferencesSection from './AgenticNotificationPreferencesSection';
import DocumentDeliverySettingsCard, {
  DeliveryFormState,
  getInitialDeliveryState,
  toDocumentDeliverySettings,
  validateDeliveryState,
} from './DocumentDeliverySettingsCard';

export type UserEmailPreference = {
  email: string;
  userId: string;
  isInvitedUser: boolean;
  isStatementsEmailOptOut: boolean;
  isPaymentRemindersEmailOptOut: boolean;
  isInvoiceEmailOptOut?: boolean;
};

interface BuyerReminderSettings {
  includeAttachments: boolean;
  numberOfDaysFromCurrentDateToDueDate: number[];
  numberOfDaysFromCurrentDateToCreditHoldDate: number[];
}

const getInitialBuyerSettings = (buyer?: Buyer | null): BuyerReminderSettings => ({
  includeAttachments: buyer?.invoicePaymentReminderSettings?.includeAttachments ?? false,
  numberOfDaysFromCurrentDateToDueDate:
    buyer?.invoicePaymentReminderSettings?.numberOfDaysFromCurrentDateToDueDate ?? [],
  numberOfDaysFromCurrentDateToCreditHoldDate:
    buyer?.invoicePaymentReminderSettings?.numberOfDaysFromCurrentDateToCreditHoldDate ?? [],
});

function SupplierCustomerEmailPreferencesModal({
  open,
  initialUsers,
  buyer,
  handleClose,
}: {
  open: boolean;
  initialUsers: UserEmailPreference[];
  buyer?: Buyer | null;
  handleClose: (isUpdated?: boolean) => void;
}) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const isInvoicingEnabled = !!currentSupplier?.invoicingSettings?.dillDoesInvoicing;
  const isMailAndFaxAvailable = currentSupplier?.isMailAndFaxDeliveryEnabled === true;

  const [users, setUsers] = useState<UserEmailPreference[]>(initialUsers);
  const [isSaving, setIsSaving] = useState(false);

  // Invoice payment reminder settings
  const [reminderSettings, setReminderSettings] = useState<BuyerReminderSettings>(() =>
    getInitialBuyerSettings(buyer)
  );
  const [initialReminderSettings, setInitialReminderSettings] = useState<BuyerReminderSettings>(
    () => getInitialBuyerSettings(buyer)
  );
  const [dueDateDayInput, setDueDateDayInput] = useState('');
  const [creditHoldDateDayInput, setCreditHoldDateDayInput] = useState('');

  // Discount reminder state
  const [localEnabled, setLocalEnabled] = useState(false);
  const [initialEnabled, setInitialEnabled] = useState(false);
  const [localDays, setLocalDays] = useState<number[]>([]);
  const [initialDays, setInitialDays] = useState<number[]>([]);
  const [dayInput, setDayInput] = useState('');
  const [inputError, setInputError] = useState('');

  // Credit limit reminder state
  const [localCreditLimitEnabled, setLocalCreditLimitEnabled] = useState(false);
  const [initialCreditLimitEnabled, setInitialCreditLimitEnabled] = useState(false);
  const [localThreshold, setLocalThreshold] = useState('');
  const [initialThreshold, setInitialThreshold] = useState('');
  const [thresholdError, setThresholdError] = useState('');

  // Invoice / statement delivery and buyer-level reminder switch
  const [delivery, setDelivery] = useState<DeliveryFormState>(() => getInitialDeliveryState(buyer));
  const [initialDelivery, setInitialDelivery] = useState<DeliveryFormState>(delivery);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [localRemindersEnabled, setLocalRemindersEnabled] = useState(true);
  const [initialRemindersEnabled, setInitialRemindersEnabled] = useState(true);

  // Agentic AI notification preferences state - each reminder type has its own independent
  // autonomy level, so a supplier can e.g. run due-date reminders fully agentic while keeping
  // credit-hold reminders manual. Slot content stays one combined array (matches the persisted
  // shape), each section below just filters/merges its own type in and out of it.
  const [aiModeDueDate, setAiModeDueDate] = useState<AiNotificationMode>('MANUAL');
  const [initialAiModeDueDate, setInitialAiModeDueDate] = useState<AiNotificationMode>('MANUAL');
  const [aiModeCreditHold, setAiModeCreditHold] = useState<AiNotificationMode>('MANUAL');
  const [initialAiModeCreditHold, setInitialAiModeCreditHold] =
    useState<AiNotificationMode>('MANUAL');
  const [aiModeDiscount, setAiModeDiscount] = useState<AiNotificationMode>('MANUAL');
  const [initialAiModeDiscount, setInitialAiModeDiscount] = useState<AiNotificationMode>('MANUAL');
  const [aiSlots, setAiSlots] = useState<AiNotificationSlot[]>([]);
  const [initialAiSlots, setInitialAiSlots] = useState<AiNotificationSlot[]>([]);
  const [isGeneratingDueDate, setIsGeneratingDueDate] = useState(false);
  const [isGeneratingCreditHold, setIsGeneratingCreditHold] = useState(false);
  const [isGeneratingDiscount, setIsGeneratingDiscount] = useState(false);

  useEffect(() => {
    setUsers(initialUsers);
    const newSettings = getInitialBuyerSettings(buyer);
    setReminderSettings(newSettings);
    setInitialReminderSettings(newSettings);
    setDueDateDayInput('');
    setCreditHoldDateDayInput('');

    if (buyer) {
      const dr = buyer.discountReminderSettings;
      setLocalEnabled(dr?.isDiscountReminderEnabled ?? false);
      setInitialEnabled(dr?.isDiscountReminderEnabled ?? false);
      const days = dr?.numberOfDaysBeforeDiscountDate ?? [];
      setLocalDays([...days].sort((a, b) => a - b));
      setInitialDays([...days].sort((a, b) => a - b));
      setDayInput('');
      setInputError('');

      const cl = buyer.creditLimitReminderSettings;
      setLocalCreditLimitEnabled(cl?.isCreditLimitReminderEnabled ?? false);
      setInitialCreditLimitEnabled(cl?.isCreditLimitReminderEnabled ?? false);
      const pct = cl?.creditLimitThresholdPercent?.toString() ?? '';
      setLocalThreshold(pct);
      setInitialThreshold(pct);
      setThresholdError('');

      const nextDelivery = getInitialDeliveryState(buyer);
      setDelivery(nextDelivery);
      setInitialDelivery(nextDelivery);
      setDeliveryError(null);
      setLocalRemindersEnabled(!buyer.isPaymentRemindersDisabled);
      setInitialRemindersEnabled(!buyer.isPaymentRemindersDisabled);

      const aiSettings = buyer.aiNotificationSettings;
      const nextAiModeDueDate = aiSettings?.dueDateMode ?? 'MANUAL';
      const nextAiModeCreditHold = aiSettings?.creditHoldMode ?? 'MANUAL';
      const nextAiModeDiscount = aiSettings?.discountMode ?? 'MANUAL';
      const nextAiSlots = aiSettings?.slots ?? [];
      setAiModeDueDate(nextAiModeDueDate);
      setInitialAiModeDueDate(nextAiModeDueDate);
      setAiModeCreditHold(nextAiModeCreditHold);
      setInitialAiModeCreditHold(nextAiModeCreditHold);
      setAiModeDiscount(nextAiModeDiscount);
      setInitialAiModeDiscount(nextAiModeDiscount);
      setAiSlots(nextAiSlots);
      setInitialAiSlots(nextAiSlots);
    }
  }, [initialUsers, buyer, open, isInvoicingEnabled]);

  // Change detection
  const reminderSettingsChanged = useMemo(
    () =>
      reminderSettings.includeAttachments !== initialReminderSettings.includeAttachments ||
      JSON.stringify(reminderSettings.numberOfDaysFromCurrentDateToDueDate) !==
        JSON.stringify(initialReminderSettings.numberOfDaysFromCurrentDateToDueDate) ||
      JSON.stringify(reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate) !==
        JSON.stringify(initialReminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate),
    [reminderSettings, initialReminderSettings]
  );

  const emailPreferencesChanged = useMemo(() => {
    const initialUserMap = new Map(initialUsers.map((u) => [u.userId, u]));
    return users.some((currentUser) => {
      const initialUser = initialUserMap.get(currentUser.userId);
      if (!initialUser) return true;
      return (
        currentUser.isStatementsEmailOptOut !== initialUser.isStatementsEmailOptOut ||
        currentUser.isPaymentRemindersEmailOptOut !== initialUser.isPaymentRemindersEmailOptOut ||
        !!currentUser.isInvoiceEmailOptOut !== !!initialUser.isInvoiceEmailOptOut
      );
    });
  }, [users, initialUsers]);

  const hasDiscountEnabledChanged = localEnabled !== initialEnabled;
  const hasDiscountDaysChanged = JSON.stringify(localDays) !== JSON.stringify(initialDays);
  const hasCreditLimitEnabledChanged = localCreditLimitEnabled !== initialCreditLimitEnabled;
  const hasThresholdChanged = localThreshold !== initialThreshold;
  // Compared on the settings that would be saved, so rearranging the form without changing what
  // gets sent where doesn't count as a change.
  const invoiceDeliverySettings = toDocumentDeliverySettings(delivery, 'INVOICE');
  const statementDeliverySettings = toDocumentDeliverySettings(delivery, 'STATEMENT');
  const hasInvoiceDeliveryChanged =
    isInvoicingEnabled &&
    JSON.stringify(invoiceDeliverySettings) !==
      JSON.stringify(toDocumentDeliverySettings(initialDelivery, 'INVOICE'));
  const hasStatementDeliveryChanged =
    JSON.stringify(statementDeliverySettings) !==
    JSON.stringify(toDocumentDeliverySettings(initialDelivery, 'STATEMENT'));
  const hasRemindersEnabledChanged = localRemindersEnabled !== initialRemindersEnabled;

  const hasAiModeChanged =
    aiModeDueDate !== initialAiModeDueDate ||
    aiModeCreditHold !== initialAiModeCreditHold ||
    aiModeDiscount !== initialAiModeDiscount;
  const hasAiSlotsChanged = JSON.stringify(aiSlots) !== JSON.stringify(initialAiSlots);

  const hasAnyChanges =
    emailPreferencesChanged ||
    reminderSettingsChanged ||
    hasDiscountEnabledChanged ||
    hasDiscountDaysChanged ||
    hasCreditLimitEnabledChanged ||
    hasThresholdChanged ||
    hasInvoiceDeliveryChanged ||
    hasStatementDeliveryChanged ||
    hasRemindersEnabledChanged ||
    hasAiModeChanged ||
    hasAiSlotsChanged;

  // User email preference handlers
  const handleToggleStatementEmail = (userId: string) => {
    setUsers(
      users.map((u) =>
        u.userId === userId ? { ...u, isStatementsEmailOptOut: !u.isStatementsEmailOptOut } : u
      )
    );
  };

  const handleTogglePaymentReminderEmail = (userId: string) => {
    setUsers(
      users.map((u) =>
        u.userId === userId
          ? { ...u, isPaymentRemindersEmailOptOut: !u.isPaymentRemindersEmailOptOut }
          : u
      )
    );
  };

  const handleToggleInvoiceEmail = (userId: string) => {
    setUsers(
      users.map((u) =>
        u.userId === userId ? { ...u, isInvoiceEmailOptOut: !u.isInvoiceEmailOptOut } : u
      )
    );
  };

  // Invoice payment reminder handlers
  const addDueDateDay = () => {
    const day = parseInt(dueDateDayInput.trim());
    if (!isNaN(day) && !reminderSettings.numberOfDaysFromCurrentDateToDueDate?.includes(day)) {
      setReminderSettings({
        ...reminderSettings,
        numberOfDaysFromCurrentDateToDueDate: [
          ...(reminderSettings.numberOfDaysFromCurrentDateToDueDate || []),
          day,
        ].sort((a, b) => a - b),
      });
      setDueDateDayInput('');
    }
  };

  const removeDueDateDay = (dayToRemove: number) => {
    setReminderSettings({
      ...reminderSettings,
      numberOfDaysFromCurrentDateToDueDate:
        reminderSettings.numberOfDaysFromCurrentDateToDueDate.filter((day) => day !== dayToRemove),
    });
  };

  const addCreditHoldDateDay = () => {
    const day = parseInt(creditHoldDateDayInput.trim());
    if (
      !isNaN(day) &&
      !reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate?.includes(day)
    ) {
      setReminderSettings({
        ...reminderSettings,
        numberOfDaysFromCurrentDateToCreditHoldDate: [
          ...(reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate || []),
          day,
        ].sort((a, b) => a - b),
      });
      setCreditHoldDateDayInput('');
    }
  };

  const removeCreditHoldDateDay = (dayToRemove: number) => {
    setReminderSettings({
      ...reminderSettings,
      numberOfDaysFromCurrentDateToCreditHoldDate:
        reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate.filter(
          (day) => day !== dayToRemove
        ),
    });
  };

  // Discount reminder handlers
  const handleAddDay = () => {
    const parsed = parseInt(dayInput, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputError('Enter a non-negative number');
      return;
    }
    if (localDays.includes(parsed)) {
      setInputError('Day already added');
      return;
    }
    setLocalDays((prev) => [...prev, parsed].sort((a, b) => a - b));
    setDayInput('');
    setInputError('');
  };

  const handleRemoveDay = (day: number) => {
    setLocalDays((prev) => prev.filter((d) => d !== day));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddDay();
    }
  };

  const handleSave = async () => {
    if (!buyer) {
      openDillToast({ message: 'Buyer not selected', type: 'ERROR' });
      return;
    }

    // Validate threshold when credit limit reminders are enabled
    if (localCreditLimitEnabled) {
      const pct = parseInt(localThreshold, 10);
      if (isNaN(pct) || pct <= 0 || pct > 100) {
        setThresholdError('Enter a percentage between 1 and 100');
        return;
      }
    }

    const nextDeliveryError =
      hasInvoiceDeliveryChanged || hasStatementDeliveryChanged
        ? validateDeliveryState(delivery, isInvoicingEnabled)
        : null;
    setDeliveryError(nextDeliveryError);
    if (nextDeliveryError) return;

    setIsSaving(true);

    // invoiceOptOutEmails is keyed by email and may hold addresses that aren't current users, so
    // only rewrite the entries for the users shown here and keep the rest.
    const shownUserEmails = new Set(users.map((u) => u.email.toLowerCase()));
    const invoiceOptOutEmails = [
      ...(buyer.invoiceOptOutEmails || []).filter(
        (email): email is string => !!email && !shownUserEmails.has(email.toLowerCase())
      ),
      ...users.filter((u) => u.isInvoiceEmailOptOut).map((u) => u.email),
    ];

    const invoiceStatementEmailsOptOutUserIds = users
      .filter((u) => u.isStatementsEmailOptOut)
      .map((u) => u.userId);

    const invoicePaymentRemindersEmailsOptOutUserIds = users
      .filter((u) => u.isPaymentRemindersEmailOptOut)
      .map((u) => u.userId);

    try {
      const result = await dispatch(
        updateBuyerEmailPreferences({
          supplierId: currentSupplier?.id || '',
          buyerId: buyer.id,
          ...(emailPreferencesChanged && {
            invoiceStatementEmailsOptOutUserIds,
            invoicePaymentRemindersEmailsOptOutUserIds,
            invoiceOptOutEmails,
          }),
          ...(reminderSettingsChanged && {
            invoicePaymentReminderSettings: reminderSettings,
          }),
          ...(hasDiscountEnabledChanged || hasDiscountDaysChanged
            ? {
                discountReminderSettings: {
                  isDiscountReminderEnabled: localEnabled,
                  numberOfDaysBeforeDiscountDate: localDays,
                },
              }
            : {}),
          ...(hasCreditLimitEnabledChanged || hasThresholdChanged
            ? {
                creditLimitReminderSettings: {
                  isCreditLimitReminderEnabled: localCreditLimitEnabled,
                  creditLimitThresholdPercent: parseInt(localThreshold, 10) || 80,
                },
              }
            : {}),
          ...(hasInvoiceDeliveryChanged && { invoiceDeliverySettings }),
          ...(hasStatementDeliveryChanged && { statementDeliverySettings }),
          ...(hasRemindersEnabledChanged && {
            isPaymentRemindersDisabled: !localRemindersEnabled,
          }),
          ...((hasAiModeChanged || hasAiSlotsChanged) && {
            aiNotificationSettings: {
              dueDateMode: aiModeDueDate,
              creditHoldMode: aiModeCreditHold,
              discountMode: aiModeDiscount,
              slots: aiSlots,
            },
          }),
        })
      );

      if (result.type === 'supplierBuyers/updateBuyerEmailPreferences/fulfilled') {
        // Update initial state to reflect saved values
        setInitialReminderSettings({ ...reminderSettings });
        setInitialEnabled(localEnabled);
        setInitialDays([...localDays]);
        setInitialCreditLimitEnabled(localCreditLimitEnabled);
        setInitialThreshold(localThreshold);
        setInitialDelivery(delivery);
        setInitialRemindersEnabled(localRemindersEnabled);
        setInitialAiModeDueDate(aiModeDueDate);
        setInitialAiModeCreditHold(aiModeCreditHold);
        setInitialAiModeDiscount(aiModeDiscount);
        setInitialAiSlots(aiSlots);

        openDillToast({ message: 'Preferences updated successfully', type: 'SUCCESS' });
        handleClose(true);
      } else {
        openDillToast({ message: 'Failed to update preferences', type: 'ERROR' });
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
      openDillToast({ message: 'Failed to update preferences', type: 'ERROR' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateAiRecommendation = async (reminderType: AiNotificationReminderType) => {
    if (!buyer) {
      openDillToast({ message: 'Buyer not selected', type: 'ERROR' });
      return;
    }

    const setGenerating =
      reminderType === 'DUE_DATE'
        ? setIsGeneratingDueDate
        : reminderType === 'CREDIT_HOLD'
        ? setIsGeneratingCreditHold
        : setIsGeneratingDiscount;
    const mode =
      reminderType === 'DUE_DATE'
        ? aiModeDueDate
        : reminderType === 'CREDIT_HOLD'
        ? aiModeCreditHold
        : aiModeDiscount;

    setGenerating(true);
    try {
      const result = await dispatch(
        generateAiNotificationRecommendation({
          supplierId: currentSupplier?.id || '',
          buyerId: buyer.id,
        })
      );

      const recommendation = (
        result.payload as {
          recommendation?: {
            dueDateOffsets: number[];
            creditHoldOffsets: number[];
            discountOffsets: number[];
            slots: typeof aiSlots;
          };
        } | null
      )?.recommendation;

      if (
        result.type !== 'supplierBuyers/generateAiNotificationRecommendation/fulfilled' ||
        !recommendation
      ) {
        openDillToast({ message: 'AI recommendation failed. Please try again.', type: 'ERROR' });
        return;
      }

      // Only apply the slice of the (combined) recommendation relevant to this reminder type -
      // the other two types' cadence/slots are left exactly as they are.
      const typeOffsets = (
        reminderType === 'DUE_DATE'
          ? recommendation.dueDateOffsets
          : reminderType === 'CREDIT_HOLD'
          ? recommendation.creditHoldOffsets
          : recommendation.discountOffsets
      )
        .slice()
        .sort((a, b) => a - b);
      const typeSlots = (recommendation.slots ?? []).filter((s) => s.reminderType === reminderType);
      const nextAiSlots = [...aiSlots.filter((s) => s.reminderType !== reminderType), ...typeSlots];

      let nextReminderSettings = reminderSettings;
      let nextDiscountOffsets = localDays;
      let nextDiscountEnabled = localEnabled;

      if (reminderType === 'DUE_DATE') {
        nextReminderSettings = {
          ...reminderSettings,
          numberOfDaysFromCurrentDateToDueDate: typeOffsets,
        };
        setReminderSettings(nextReminderSettings);
      } else if (reminderType === 'CREDIT_HOLD') {
        nextReminderSettings = {
          ...reminderSettings,
          numberOfDaysFromCurrentDateToCreditHoldDate: typeOffsets,
        };
        setReminderSettings(nextReminderSettings);
      } else {
        nextDiscountOffsets = typeOffsets;
        nextDiscountEnabled = typeOffsets.length > 0 ? true : localEnabled;
        setLocalDays(nextDiscountOffsets);
        if (typeOffsets.length > 0) setLocalEnabled(true);
      }

      setAiSlots(nextAiSlots);

      if (mode === 'FULLY_AGENTIC') {
        const saveResult = await dispatch(
          updateBuyerEmailPreferences({
            supplierId: currentSupplier?.id || '',
            buyerId: buyer.id,
            invoicePaymentReminderSettings: nextReminderSettings,
            discountReminderSettings: {
              isDiscountReminderEnabled: nextDiscountEnabled,
              numberOfDaysBeforeDiscountDate: nextDiscountOffsets,
            },
            aiNotificationSettings: {
              dueDateMode: aiModeDueDate,
              creditHoldMode: aiModeCreditHold,
              discountMode: aiModeDiscount,
              slots: nextAiSlots,
            },
          })
        );

        if (saveResult.type === 'supplierBuyers/updateBuyerEmailPreferences/fulfilled') {
          setInitialReminderSettings(nextReminderSettings);
          setInitialDays(nextDiscountOffsets);
          setInitialEnabled(nextDiscountEnabled);
          setInitialAiModeDueDate(aiModeDueDate);
          setInitialAiModeCreditHold(aiModeCreditHold);
          setInitialAiModeDiscount(aiModeDiscount);
          setInitialAiSlots(nextAiSlots);
          openDillToast({
            message: 'Dill AI generated and applied the notification plan',
            type: 'SUCCESS',
          });
        } else {
          openDillToast({
            message: 'Generated, but saving failed — click Save changes to retry.',
            type: 'ERROR',
          });
        }
      } else {
        openDillToast({
          message: 'Dill AI generated a recommended plan — review and save changes to apply it',
          type: 'SUCCESS',
        });
      }
    } catch (error) {
      console.error('Failed to generate AI recommendation:', error);
      openDillToast({ message: 'AI recommendation failed. Please try again.', type: 'ERROR' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <DillModal
      open={open}
      onClose={() => {
        if (!isSaving) handleClose();
      }}
      title="Notification Preferences"
      subtitle={buyer?.name || undefined}
      maxWidth="640px"
      contentClassName="flex-1 min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-4"
      footer={
        <>
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={() => handleClose()}
            isDisabled={isSaving}
          />
          <DillButton
            label="Save changes"
            variant="primary"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || !hasAnyChanges}
          />
        </>
      }>
      {/* Section 1: User Email Preferences */}
      <DillCard className="bg-white">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-purple-50">
            <Envelope size={16} color={COLORS.PRIMARY_500} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">User Email Preferences</h4>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Control which users receive {isInvoicingEnabled ? 'invoice, ' : ''}statement and
              payment reminder emails.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          {users.length === 0 ? (
            <p className="text-xs text-gray-400">No users found for this customer.</p>
          ) : (
            users.map((userPref) => (
              <div
                key={userPref.userId}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900">{userPref.email}</span>
                  {userPref.isInvitedUser && <span className="text-xs text-gray-400">Invited</span>}
                </div>
                {isInvoicingEnabled && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">Receive Invoice Emails</span>
                    <DillToggle
                      value={!userPref.isInvoiceEmailOptOut}
                      onChange={() => handleToggleInvoiceEmail(userPref.userId)}
                    />
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Receive Statement Emails</span>
                  <DillToggle
                    value={!userPref.isStatementsEmailOptOut}
                    onChange={() => handleToggleStatementEmail(userPref.userId)}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-gray-500">Receive Payment Reminder Emails</span>
                  <DillToggle
                    value={!userPref.isPaymentRemindersEmailOptOut}
                    onChange={() => handleTogglePaymentReminderEmail(userPref.userId)}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </DillCard>

      <DocumentDeliverySettingsCard
        buyer={buyer}
        value={delivery}
        onChange={(next) => {
          setDelivery(next);
          setDeliveryError(null);
        }}
        disabled={isSaving}
        isInvoicingEnabled={isInvoicingEnabled}
        isMailAndFaxAvailable={isMailAndFaxAvailable}
        error={deliveryError}
      />

      {/* Section 2: Invoice Payment Reminder Settings */}
      <DillCard className="bg-white">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-green-50">
            <Bell size={16} color={COLORS.SUCCESS_700} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">
              Invoice Payment Reminder Settings
            </h4>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Configure when payment reminder emails are sent relative to due dates and credit hold
              dates.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Send automated reminders</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Turn off to stop all automated payment reminders for this customer, including for
                users added later. Manual reminders can still be sent.
              </p>
            </div>
            <DillToggle
              value={localRemindersEnabled}
              onChange={(v) => setLocalRemindersEnabled(v)}
            />
          </div>
        </div>

        {/* Include Attachments toggle */}
        <div className="mt-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Include attachments</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Attach invoice documents to reminder emails.
              </p>
            </div>
            <DillToggle
              value={reminderSettings.includeAttachments}
              onChange={() =>
                setReminderSettings({
                  ...reminderSettings,
                  includeAttachments: !reminderSettings.includeAttachments,
                })
              }
            />
          </div>
        </div>

        {/* Days from Current Date to Due Date */}
        <div className="mt-4">
          {aiModeDueDate !== 'FULLY_AGENTIC' && (
            <>
              <p className="text-xs font-medium text-gray-600 mb-2">
                Days from current date to due date (negative means past due)
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <DillInput
                    value={dueDateDayInput}
                    onChange={(v) => setDueDateDayInput(v)}
                    placeholder="Enter number of days"
                    type="number"
                    disabled={isSaving}
                  />
                </div>
                <DillButton
                  label="Add"
                  variant="secondary"
                  onClick={addDueDateDay}
                  isDisabled={!dueDateDayInput || isSaving}
                  height={36}
                />
              </div>

              {reminderSettings.numberOfDaysFromCurrentDateToDueDate.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {[...reminderSettings.numberOfDaysFromCurrentDateToDueDate]
                    .sort((a, b) => b - a)
                    .map((day) => (
                      <span
                        key={day}
                        className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700">
                        {day > 0
                          ? `${day} day${day === 1 ? '' : 's'} before due date`
                          : `${Math.abs(day)} day${Math.abs(day) === 1 ? '' : 's'} after due date`}
                        <button
                          type="button"
                          onClick={() => removeDueDateDay(day)}
                          className="ml-0.5 rounded-full p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                          <XIcon size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </>
          )}

          {APP_ENVIRONMENT !== 'production' && (
            <AgenticNotificationPreferencesSection
              embedded
              reminderType="DUE_DATE"
              supplierId={currentSupplier?.id || ''}
              buyerId={buyer?.id}
              disabled={isSaving}
              mode={aiModeDueDate}
              onModeChange={setAiModeDueDate}
              slots={aiSlots.filter((s) => s.reminderType === 'DUE_DATE')}
              onSlotsChange={(typeSlots) =>
                setAiSlots([...aiSlots.filter((s) => s.reminderType !== 'DUE_DATE'), ...typeSlots])
              }
              offsets={reminderSettings.numberOfDaysFromCurrentDateToDueDate}
              isGenerating={isGeneratingDueDate}
              onGenerate={() => handleGenerateAiRecommendation('DUE_DATE')}
            />
          )}
        </div>

        {/* Days from Current Date to Credit Hold Date */}
        <div className="mt-4">
          {aiModeCreditHold !== 'FULLY_AGENTIC' && (
            <>
              <p className="text-xs font-medium text-gray-600 mb-2">
                Days from current date to credit hold date (negative means past due)
              </p>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <DillInput
                    value={creditHoldDateDayInput}
                    onChange={(v) => setCreditHoldDateDayInput(v)}
                    placeholder="Enter number of days"
                    type="number"
                    disabled={isSaving}
                  />
                </div>
                <DillButton
                  label="Add"
                  variant="secondary"
                  onClick={addCreditHoldDateDay}
                  isDisabled={!creditHoldDateDayInput || isSaving}
                  height={36}
                />
              </div>

              {reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {[...reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate]
                    .sort((a, b) => b - a)
                    .map((day) => (
                      <span
                        key={day}
                        className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700">
                        {day > 0
                          ? `${day} day${day === 1 ? '' : 's'} before credit hold date`
                          : `${Math.abs(day)} day${
                              Math.abs(day) === 1 ? '' : 's'
                            } after credit hold date`}
                        <button
                          type="button"
                          onClick={() => removeCreditHoldDateDay(day)}
                          className="ml-0.5 rounded-full p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                          <XIcon size={12} weight="bold" />
                        </button>
                      </span>
                    ))}
                </div>
              )}
            </>
          )}

          {APP_ENVIRONMENT !== 'production' && (
            <AgenticNotificationPreferencesSection
              embedded
              reminderType="CREDIT_HOLD"
              supplierId={currentSupplier?.id || ''}
              buyerId={buyer?.id}
              disabled={isSaving}
              mode={aiModeCreditHold}
              onModeChange={setAiModeCreditHold}
              slots={aiSlots.filter((s) => s.reminderType === 'CREDIT_HOLD')}
              onSlotsChange={(typeSlots) =>
                setAiSlots([
                  ...aiSlots.filter((s) => s.reminderType !== 'CREDIT_HOLD'),
                  ...typeSlots,
                ])
              }
              offsets={reminderSettings.numberOfDaysFromCurrentDateToCreditHoldDate}
              isGenerating={isGeneratingCreditHold}
              onGenerate={() => handleGenerateAiRecommendation('CREDIT_HOLD')}
            />
          )}
        </div>
      </DillCard>

      {/* Section 3: Discount Deadline Reminders */}
      <DillCard className="bg-white">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-blue-50">
            <Percent size={16} color={COLORS.PRIMARY_500} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">Discount Deadline Reminders</h4>
            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
              Send reminder emails to the buyer before discount dates on invoices.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900">Enable discount reminders</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Buyers will receive reminder emails before discount deadlines.
              </p>
            </div>
            <DillToggle value={localEnabled} onChange={(v) => setLocalEnabled(v)} />
          </div>
        </div>

        {localEnabled && (
          <div className="mt-4">
            {aiModeDiscount !== 'FULLY_AGENTIC' && (
              <>
                <p className="text-xs font-medium text-gray-600 mb-2">Days before discount date</p>

                <div className="flex items-center gap-2">
                  <div className="flex-1" onKeyDown={handleKeyDown}>
                    <DillInput
                      value={dayInput}
                      onChange={(v) => {
                        setDayInput(v);
                        setInputError('');
                      }}
                      placeholder="e.g. 3"
                      type="number"
                      disabled={isSaving}
                      error={inputError}
                    />
                  </div>
                  <DillButton
                    label="Add"
                    variant="secondary"
                    onClick={handleAddDay}
                    isDisabled={!dayInput || isSaving}
                    height={36}
                  />
                </div>

                {localDays.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[...localDays]
                      .sort((a, b) => b - a)
                      .map((day) => (
                        <span
                          key={day}
                          className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5 text-xs font-medium text-blue-700">
                          {day} day{day !== 1 ? 's' : ''} before discount
                          <button
                            type="button"
                            onClick={() => handleRemoveDay(day)}
                            className="ml-0.5 rounded-full p-0.5 text-blue-400 hover:bg-blue-100 hover:text-blue-600 transition-colors">
                            <XIcon size={12} weight="bold" />
                          </button>
                        </span>
                      ))}
                  </div>
                )}

                {localDays.length === 0 && (
                  <p className="mt-2 text-xs text-gray-400">
                    No days configured. Add at least one day to receive reminders.
                  </p>
                )}
              </>
            )}

            {APP_ENVIRONMENT !== 'production' && (
              <AgenticNotificationPreferencesSection
                embedded
                reminderType="DISCOUNT"
                supplierId={currentSupplier?.id || ''}
                buyerId={buyer?.id}
                disabled={isSaving}
                mode={aiModeDiscount}
                onModeChange={setAiModeDiscount}
                slots={aiSlots.filter((s) => s.reminderType === 'DISCOUNT')}
                onSlotsChange={(typeSlots) =>
                  setAiSlots([
                    ...aiSlots.filter((s) => s.reminderType !== 'DISCOUNT'),
                    ...typeSlots,
                  ])
                }
                offsets={localDays}
                isGenerating={isGeneratingDiscount}
                onGenerate={() => handleGenerateAiRecommendation('DISCOUNT')}
              />
            )}
          </div>
        )}
      </DillCard>

      {/* Section 4: Credit Limit Reminders */}
      {APP_ENVIRONMENT !== 'production' && (
        <DillCard className="bg-white">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-orange-50">
              <ChartBar size={16} color={COLORS.WARNING_700} weight="bold" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-sm font-semibold text-gray-900">Credit Limit Reminders</h4>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                Notify the buyer when their outstanding balance nears their credit limit.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">Enable credit limit reminders</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Buyers will receive a reminder when they approach their credit limit.
                </p>
              </div>
              <DillToggle
                value={localCreditLimitEnabled}
                onChange={(v) => setLocalCreditLimitEnabled(v)}
              />
            </div>
          </div>

          {localCreditLimitEnabled && (
            <div className="mt-4">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium text-gray-600 mb-2">Threshold percentage</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 max-w-[200px]">
                    <DillInput
                      value={localThreshold}
                      onChange={(v) => {
                        setLocalThreshold(v);
                        setThresholdError('');
                      }}
                      placeholder="e.g. 80"
                      type="number"
                      disabled={isSaving}
                      error={thresholdError}
                    />
                  </div>
                  <span className="text-sm text-gray-500">%</span>
                </div>
                <p className="text-xs text-gray-400 mt-1.5">
                  Buyer will be reminded when their balance reaches this percentage of the credit
                  limit.
                </p>
              </div>
            </div>
          )}
        </DillCard>
      )}
    </DillModal>
  );
}

export default SupplierCustomerEmailPreferencesModal;
